import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { Button } from '@shared/components/ui/button';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteLocation } from '../../hooks/useDeleteLocation';
import { Location } from '../../models/Location';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithLocation: (location: Location) => void;
}

const DeleteLocationModal = forwardRef<ExtendedModalCenterRef, {}>((props, ref) => {
  const { t } = useTranslation('locationsModule');
  const { toast } = useToast();
  const deleteLocationMutation = useDeleteLocation();

  // 🎯 Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal - NO causa re-render del padre
  const [location, setLocation] = useState<Location | null>(null);

  const resetForm = () => {
    setLocation(null);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleDelete = async () => {
    try {
      if (!location) {
        toast({
          title: t('errors.generic'),
          description: t('modal.delete.error_no_selection'),
          variant: 'destructive',
        });
        return;
      }

      await deleteLocationMutation.mutateAsync(location.id);

      toast({
        title: t('modal.delete.success_title'),
        description: t('modal.delete.success_description'),
      });

      resetForm();
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.delete.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  // 🎯 ÚNICO useCallback necesario - Se pasa a useImperativeHandle
  const openWithLocation = useCallback((locationData: Location) => {
    setLocation(locationData);
    internalModalRef.current?.openModal();
  }, []);

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithLocation, // ← Método nuevo para uso directo
    }),
    [openWithLocation],
  );

  const isLoading = deleteLocationMutation.isPending;

  return (
    <ModalCenter ref={internalModalRef} title={t('modal.delete.title')} type="error">
      <div className="space-y-4">
        <p className="text-sm text-text mt-2">
          {t('modal.delete.confirmation')} <strong>{location?.code}</strong>?
        </p>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={handleCancel}
            name="cancel"
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            {t('button.cancel')}
            <EscIcon className="ml-1 !h-4 !w-auto" />
          </Button>
          <Button
            variant="primary"
            size="lg"
            name="delete-btn-confirm"
            onClick={handleDelete}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            {isLoading ? t('button.deleting') : t('button.delete')}
            <EnterIcon className="ml-1 !h-3.5 !w-auto" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

export default DeleteLocationModal;
DeleteLocationModal.displayName = 'DeleteLocationModal';
